class ApexSwiper {
  #swiper;
  #wrapper;
  #scorll;
  #slides;
  #groupCount;
  #pagination;
  #bullets;
  #prevButton;
  #nextButton;
  #scrollAnimated;
  #currentIndex = 0;
  #limit = 1;
  #startX = 0;
  #currentTranslate = 0;
  #isDragging = false;
  #transition;

  constructor(swiperElement, initOptions = {}) {
    if (!swiperElement || !swiperElement.classList.contains("apex-swiper")) {
      throw new Error("请传入一个包含类名 apex-swiper 的 DOM 元素");
    }

    this.#swiper = swiperElement;
    this.#wrapper = this.#swiper.querySelector(".apex-swiper-wrapper");
    this.#scorll = this.#swiper.querySelector(".apex-swiper-scroll");
    this.#slides = Array.from(this.#swiper.querySelectorAll(".swiper-slide"));

    // 初始化
    this.#init(initOptions);
  }

  #init({ mobileOnly = false, autoplay = true, hasButtons = true }) {
    if (mobileOnly && window.innerWidth > 768) {
      return;
    }
    this.#calculateLimit();
    if (this.#groupCount < 2) {
      return;
    }
    this.#createBullets();
    hasButtons && this.#createOperationButtons();
    autoplay && this.#createScrollAnimated();
    this.#updateVisibility();
    this.#bindEvents();
    autoplay && this.#autoScroll();
    this.#updateActive();
  }

  /** 根据wrapper和slides的宽度，计算limit的值 */
  #calculateLimit() {
    const wrapperWidth = this.#wrapper.getBoundingClientRect().width;
    const slideWidth = this.#slides[0].getBoundingClientRect().width;
    const limit = Math.floor(wrapperWidth / slideWidth);
    this.#limit = limit;
    this.#groupCount = Math.ceil(this.#slides.length / this.#limit);
  }

  /** 根据slides的数量创建bullet */
  #createBullets() {
    const fragment = document.createElement("div");
    fragment.classList.add("apex-swiper-pagination");
    for (let i = 0; i < this.#groupCount; i++) {
      const bullet = document.createElement("span");
      bullet.classList.add("swiper-pagination-bullet");
      fragment.appendChild(bullet);
    }
    this.#swiper.appendChild(fragment);
    this.#pagination = fragment;
    this.#bullets = Array.from(this.#swiper.querySelectorAll(".swiper-pagination-bullet"));
  }

  /** 创建切换按钮 */
  #createOperationButtons() {
    const prevButton = document.createElement("button");
    prevButton.classList.add("circle-button", "circle-button--xs", "circle-button--fill");
    prevButton.setAttribute("is", "prev-button");
    prevButton.innerHTML = '<span class="animated-arrow animated-arrow--reverse"></span>';
    this.#swiper.appendChild(prevButton);
    this.#prevButton = prevButton;
    const nextButton = document.createElement("button");
    nextButton.classList.add("circle-button", "circle-button--xs", "circle-button--fill");
    nextButton.setAttribute("is", "next-button");
    nextButton.innerHTML = '<span class="animated-arrow"></span>';
    this.#swiper.appendChild(nextButton);
    this.#nextButton = nextButton;
  }

  #createScrollAnimated() {
    const animatedSpan = document.createElement("span");
    animatedSpan.classList.add("scroll-animated");
    this.#swiper.appendChild(animatedSpan);
    this.#scrollAnimated = animatedSpan;
  }

  #updateVisibility() {
    if (!this.#prevButton || !this.#nextButton) {
      return;
    }
    if (this.#slides.length <= this.#limit) {
      this.#pagination.style.display = "none";
      this.#prevButton.style.display = "none";
      this.#nextButton.style.display = "none";
    } else {
      this.#pagination.style.display = "";
      this.#prevButton.style.display = "";
      this.#nextButton.style.display = "";
    }
    this.#updateButtonsState();
  }

  #getScrollAreaWidth() {
    const wrapperWidth = this.#wrapper.getBoundingClientRect().width;
    const slideWidth = this.#slides[0].getBoundingClientRect().width;
    return this.#limit > 1 ? wrapperWidth : slideWidth;
  }

  #handleTouchStart(e) {
    this.#isDragging = true;
    this.#startX = e.touches ? e.touches[0].clientX : e.x;
    this.#currentTranslate = -this.#currentIndex * this.#getScrollAreaWidth();
    this.#autoScroll(false); // 停止自动轮播
    this.#transition = this.#scorll.style.transition;
    this.#scorll.style.transition = "none";
  }

  #handleTouchMove(e) {
    if (!this.#isDragging) return;

    const currentX = e.touches ? e.touches[0].clientX : e.x;
    const diff = currentX - this.#startX;
    const newTranslate = this.#currentTranslate + diff;
    
    if (Math.abs(diff) > 50) {
      e.preventDefault();
    }

    // 限制滑动范围
    if (newTranslate < -this.#getScrollAreaWidth() * (this.#groupCount - 1) || newTranslate > 0) {
      return;
    }

    this.#scorll.style.transform = `translateX(${newTranslate}px)`;

    // 判断是否超过一半宽度
    const slideWidth = this.#getScrollAreaWidth();
    if (Math.abs(diff) > slideWidth / 2) {
      const direction = diff > 0 ? -1 : 1;
      const newIndex = this.#currentIndex + direction;
      if (newIndex >= 0 && newIndex < this.#groupCount) {
        this.#updateActive(newIndex);
      }
    }
  }

  #handleTouchEnd(e) {
    if (!this.#isDragging) return;

    const endX = e.changedTouches ? e.changedTouches[0].clientX : e.x;
    const diff = endX - this.#startX;
    const slideWidth = this.#getScrollAreaWidth();

    let newIndex = this.#currentIndex;
    if (Math.abs(diff) > slideWidth / 2) {
      newIndex = diff > 0 ? this.#currentIndex - 1 : this.#currentIndex + 1;
    }

    this.#isDragging = false;
    this.#scorll.style.transition = this.#transition;
    this.scrollToIndex(newIndex);
    this.#autoScroll(); // 重新开启自动轮播
  }

  #bindTouchEvents() {
    // 如果是pc端，兼容鼠标事件
    if (window.innerWidth > 768) {
      this.#swiper.addEventListener("mousedown", this.#handleTouchStart.bind(this));
      this.#swiper.addEventListener("mousemove", this.#handleTouchMove.bind(this));
      this.#swiper.addEventListener("mouseup", this.#handleTouchEnd.bind(this));
    } else {
      this.#swiper.addEventListener("touchstart", this.#handleTouchStart.bind(this));
      this.#swiper.addEventListener("touchmove", this.#handleTouchMove.bind(this));
      this.#swiper.addEventListener("touchend", this.#handleTouchEnd.bind(this));
    }
  }

  #bindEvents() {
    this.#prevButton && this.#prevButton.addEventListener("click", () => this.scrollToIndex(this.#currentIndex - 1));
    this.#nextButton && this.#nextButton.addEventListener("click", () => this.scrollToIndex(this.#currentIndex + 1));
    this.#bindTouchEvents();
    this.#bullets.forEach((bullet, index) => {
      bullet.addEventListener("click", () => this.scrollToIndex(index));
    });

    if (this.#groupCount > 1 && this.#scrollAnimated) {
      this.#scrollAnimated.addEventListener("animationiteration", () => {
        if (this.#isDragging) return;
        this.scrollToIndex(this.#currentIndex + 1);
      });
    }
  }

  #autoScroll(enable = true) {
    if (this.#scrollAnimated) {
      this.#scrollAnimated.style.animationPlayState = enable ? "running" : "paused";
    }
  }

  scrollToIndex(index) {
    if (index < 0) index = this.#groupCount - 1;
    if (index >= this.#groupCount) index = 0;

    // this.#isScrolling = true; // 暂时禁用 scroll 事件逻辑

    // const targetSlide = this.#slides[index];
    // if (targetSlide) {
    //   targetSlide.scrollIntoView({
    //     behavior: "smooth",
    //     block: "nearest",
    //     inline: isPrev ? "start" : "end",
    //   });
    // }
    const scrollLeft = this.#getScrollAreaWidth();
    this.#scorll.style.transform = `translateX(${-scrollLeft * index}px)`;
    this.#currentIndex = index;
    this.#updateActive();
    this.#updateButtonsState();

    // 恢复 scroll 事件逻辑
    // setTimeout(() => {
    //   this.#isScrolling = false;
    // }, 300); // 时间应与 scrollIntoView 的滚动动画时长一致
  }

  #updateActive(newIndex = this.#currentIndex) {
    this.#slides.forEach((slide, index) => {
      slide.classList.toggle("active", index === newIndex);
    });

    this.#bullets.forEach((bullet, index) => {
      bullet.classList.toggle("active", index === newIndex);
    });
  }

  #updateButtonsState() {
    if (!this.#prevButton || !this.#nextButton) {
      return;
    }
    this.#prevButton.disabled = this.#currentIndex === 0;
    this.#nextButton.disabled = this.#currentIndex === this.#groupCount - 1;
  }
}

window.ApexSwiper = ApexSwiper;
